<template>
  <div
    class="fu-bulk"
    :class="{ 'fu-bulk--phone': layout === 'phone' }"
    role="region"
    aria-label="Selected patients"
    data-test="fu-bulk-bar"
  >
    <span class="fu-bulk__count" aria-live="polite">
      <fu-icon name="check" :size="15" />
      {{ count }} selected
    </span>
    <button type="button" class="fu-bulk__add" data-test="fu-bulk-add" @click="$emit('add')">
      <fu-icon name="list" :size="14" />
      {{ layout === 'phone' ? 'Add to list' : 'Add to existing list' }}
    </button>
    <button type="button" class="fu-bulk__create" data-test="fu-bulk-create" @click="$emit('create')">
      <fu-icon name="plus" :size="15" />
      Create new list
    </button>
    <button
      type="button"
      class="fu-bulk__clear"
      title="Clear selection"
      aria-label="Clear selection"
      data-test="fu-bulk-clear"
      @click="$emit('clear')"
    >
      <fu-icon name="x" :size="16" />
    </button>
  </div>
</template>

<script>
/**
 * The bulk bar — fixed at the bottom while rows are selected: "N selected", Add to existing list,
 * Create new list, clear. Presentational; the queue turns the clicks into the legacy events
 * (add-to-existing-list / create-new-list with the selection, clear-selection).
 *
 * ⛔ Position: above the app header (z 1030), below Bootstrap modals (1040) — the list dialogs the
 * buttons open must cover it. On a phone it stops 84px short of the right edge: the floating agent
 * button (z 9999, right 20px, 48px wide) and the bot toggle under it own that corner.
 */
import FuIcon from './FuIcon.vue';

export default {
  name: 'FollowupsBulkBar',
  components: { FuIcon },
  props: {
    count: { type: Number, default: 0 },
    layout: { type: String, default: 'desktop' },
  },
};
</script>

<style lang="scss" scoped>
@import './mixins';

.fu-bulk {
  @include fu-font-base;
  position: fixed;
  left: 50%;
  bottom: 24px;
  transform: translateX(-50%);
  z-index: $fu-z-bulk-bar;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  max-width: calc(100vw - 32px);
  padding: $fu-space-2 $fu-space-2 $fu-space-2 $fu-space-4;
  background: $fu-neutral-950;
  color: #ffffff;
  border-radius: $fu-radius-lg;
  box-shadow: $fu-shadow-xl;
}

// Phone (the queue's own width, or a phone-sized window): pinned left, clear of the agent corner.
@mixin bulk-phone {
  left: 8px;
  right: 84px;
  bottom: 8px;
  transform: none;
  max-width: none;
}

.fu-bulk--phone {
  @include bulk-phone;
}

@include fu-phone {
  .fu-bulk { @include bulk-phone; }
}

.fu-bulk__count {
  display: inline-flex;
  align-items: center;
  gap: $fu-space-2;
  font-size: 14px;
  font-weight: $fu-fw-bold;
  padding-right: 10px;
  border-right: 1px solid $fu-neutral-800;
  margin-right: 4px;
  white-space: nowrap;
}

.fu-bulk__add {
  @include fu-btn(inverse, sm);
  font-size: 13.5px;
  padding: 0 12px;
  gap: 6px;
}

.fu-bulk__create {
  @include fu-btn(primary, sm);
  gap: 6px;
}

.fu-bulk__clear {
  @include fu-icon-btn(34px);
  color: $fu-neutral-400;
  &:hover:not([disabled]) { background: $fu-neutral-800; color: #ffffff; }
}
</style>
